import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Redirect, useHistory, useLocation } from "react-router-dom";
import {
  FaBookOpen,
  FaHome,
  FaImage,
  FaPlus,
  FaSyncAlt,
  FaTrash,
} from "react-icons/fa";
import MyLoader from "../Spinner/MyLoader";
import SessionManager from "../Utils/SessionManager";
import myToast from "../Utils/MyToast";
import COLORS from "../resources/colors/COLORS";
import BackButton from "../ChildComponent/BackButton";
import "../css/Admin_css/admin-customize.css";
import "../css/Admin_css/payroll.css";

const API_BASE = () =>
  `${process.env.REACT_APP_SERVER_NAME}${process.env.REACT_APP_TARGET_DIR}`;

const HERO_GRADIENT = `linear-gradient(135deg, ${COLORS.primary_color_shade5} 0%, ${COLORS.primary_color_shade7} 55%, ${COLORS.primary_color_shade9} 100%)`;

const PAGE_FONT =
  'Inter, system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';

//Created By Cursor
function resolveImgUrl(path) {
  if (!path) return "";
  const trimmed = String(path).trim();
  if (!trimmed) return "";
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  const host = String(process.env.REACT_APP_SERVER_NAME || "").replace(
    /\/$/,
    ""
  );
  return `${host}/${trimmed.replace(/^\//, "")}`;
}

//Created By Cursor
function parseQuery(search) {
  const p = new URLSearchParams(search || "");
  return {
    class_id: p.get("class_id") || "",
    section_id: p.get("section_id") || "",
    session_id: p.get("session_id") || "",
    subject_id: p.get("subject_id") || "",
    subject_name: p.get("subject_name") || "",
    class_name: p.get("class_name") || "",
    date: p.get("date") || "",
    date_display: p.get("date_display") || "",
  };
}

//Created By Cursor
export default function HomeworkAssign() {
  const history = useHistory();
  const location = useLocation();
  const loggedIn = localStorage.getItem("token") != null;

  const ctx = useMemo(
    () => parseQuery(location.search),
    [location.search]
  );

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState("");
  const [homework, setHomework] = useState([]);
  const [newHomework, setNewHomework] = useState("");
  const [selectedImage, setSelectedImage] = useState(null);

  const dateLabel = ctx.date_display || ctx.date || "—";
  const classLabel = ctx.class_name || "Class";
  const subjectLabel = ctx.subject_name || "Subject";
  const contextReady = Boolean(
    ctx.class_id && ctx.section_id && ctx.subject_id && ctx.date
  );

  const loadHomework = useCallback(async () => {
    if (!contextReady) {
      setHomework([]);
      return;
    }
    setLoading(true);
    try {
      const schoolCode = SessionManager.getSchoolCode();
      const params = {
        school_code: schoolCode,
        class_id: ctx.class_id,
        section_id: ctx.section_id,
        subject_id: ctx.subject_id,
        homework_date: ctx.date,
      };
      const url = `${API_BASE()}getClassSubjectHomework.php`;
      console.log("Full URL: ", `${url}?${new URLSearchParams(params)}`);

      const response = await axios.get(url, { params }).then((res) => res.data);
      if (response?.api_status === "OK") {
        const rows = Array.isArray(response.data) ? response.data : [];
        setHomework(rows);
      } else {
        setHomework([]);
        if (response?.api_remark && !/no record/i.test(response.api_remark)) {
          myToast.notify_warning(response.api_remark);
        }
      }
    } catch (error) {
      console.error(error);
      setHomework([]);
      myToast.notify_warning("Failed to load homework.");
    } finally {
      setLoading(false);
    }
  }, [contextReady, ctx.class_id, ctx.section_id, ctx.subject_id, ctx.date]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    loadHomework();
  }, [loadHomework]);

  const handleAddHomework = async () => {
    const text = newHomework.trim();
    if (!text) {
      myToast.notify_warning("Please enter homework.");
      return;
    }
    if (!contextReady) {
      myToast.notify_warning("Missing class, subject, or date.");
      return;
    }
    if (!ctx.session_id) {
      myToast.notify_warning("Missing session. Go back and select a class.");
      return;
    }

    setSaving(true);
    try {
      const schoolCode = SessionManager.getSchoolCode();
      const params = {
        homework_date: ctx.date,
        class_id: ctx.class_id,
        section_id: ctx.section_id,
        session_id: ctx.session_id,
        subject_id: ctx.subject_id,
        homework: text,
        school_code: schoolCode,
      };
      const url = `${API_BASE()}uploadHomework.php`;
      console.log("Full URL: ", `${url}?${new URLSearchParams(params)}`);

      const response = await axios.get(url, { params }).then((res) => res.data);
      if (response?.api_status === "OK") {
        myToast.notify_success("Homework assigned.");
        setNewHomework("");
        await loadHomework();
      } else {
        myToast.notify_warning(
          response?.api_remark || "Failed to assign homework."
        );
      }
    } catch (error) {
      console.error(error);
      myToast.notify_warning("Failed to assign homework.");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteHomework = async (item) => {
    const homeworkId = item?.homework_id;
    if (!homeworkId) return;
    const ok = window.confirm("Delete this homework?");
    if (!ok) return;

    const staff = SessionManager.getStaffDetails();
    const staffId = staff?.staff_id;
    if (!staffId) {
      myToast.notify_warning("Missing staff details.");
      return;
    }

    setDeletingId(homeworkId);
    try {
      const schoolCode = SessionManager.getSchoolCode();
      const params = {
        staff_id: staffId,
        homework_id: homeworkId,
        homework_date: ctx.date,
        class_name: classLabel,
        subject_name: subjectLabel,
        school_code: schoolCode,
        session_id: ctx.session_id || "",
      };
      const url = `${API_BASE()}DeleteHomeWork.php`;
      console.log("Full URL: ", `${url}?${new URLSearchParams(params)}`);

      const response = await axios.get(url, { params }).then((res) => res.data);
      if (response?.api_status === "OK") {
        myToast.notify_success("Homework deleted.");
        await loadHomework();
      } else {
        myToast.notify_warning(
          response?.api_remark || "Failed to delete homework."
        );
      }
    } catch (error) {
      console.error(error);
      myToast.notify_warning("Failed to delete homework.");
    } finally {
      setDeletingId("");
    }
  };

  if (!loggedIn) {
    return <Redirect to="/Login" />;
  }

  const busy = loading || saving || Boolean(deletingId);

  return (
    <div
      className="content payroll-page sm-page ahw-page"
      style={{ fontFamily: PAGE_FONT }}
    >
      <div
        className="content-header d-flex justify-content-between align-items-center"
        style={{ background: COLORS.primary_color_shade5 }}
      >
        <div className="leftside-content-header d-flex align-items-center gap-2">
          <button
            type="button"
            className="qb-home-btn"
            aria-label="Home"
            onClick={() => history.push("/AdminMain")}
          >
            <FaHome />
          </button>
          <ul className="breadcrumbs mb-0">
            <li>
              <button
                type="button"
                className="ahw-crumb-link"
                onClick={() => history.push("/AdminMain/HomeWork")}
              >
                Homework
              </button>
            </li>
            <li>
              <span style={{ color: COLORS.white }}>Assign</span>
            </li>
          </ul>
        </div>
        <div className="rightside-content-header">
          <BackButton />
        </div>
      </div>

      <div className="bg_page_white_rounded box_shadow animated fadeInUp sm-shell ahw-shell">
        <header className="sm-hero" style={{ "--sm-hero-bg": HERO_GRADIENT }}>
          <div className="sm-hero__brand">
            <div className="sm-hero__icon" aria-hidden>
              <FaBookOpen />
            </div>
            <div className="min-w-0">
              <h1 className="sm-hero__title">Homework Assign</h1>
              <p className="sm-hero__sub">
                {classLabel} · {subjectLabel} · {dateLabel}
              </p>
            </div>
          </div>
          <div className="sm-hero__tools">
            <button
              type="button"
              className={`pp-icon-btn${loading ? " spinning" : ""}`}
              onClick={loadHomework}
              disabled={busy || !contextReady}
              aria-label="Refresh"
              title="Refresh"
            >
              <FaSyncAlt aria-hidden />
            </button>
          </div>
        </header>

        <div className="ahw-body">
          {!contextReady ? (
            <div className="ahw-empty">
              Open a subject from Homework calendar to assign work.
            </div>
          ) : (
            <>
              <div className="ahw-meta">
                <div className="ahw-meta__item">
                  <span>Class</span>
                  <strong>{classLabel}</strong>
                </div>
                <div className="ahw-meta__item">
                  <span>Subject</span>
                  <strong>{subjectLabel}</strong>
                </div>
                <div className="ahw-meta__item">
                  <span>Date</span>
                  <strong>{dateLabel}</strong>
                </div>
                <div className="ahw-meta__item">
                  <span>Status</span>
                  <strong>
                    {homework.length
                      ? `${homework.length} assigned`
                      : "Not assigned"}
                  </strong>
                </div>
              </div>

              <section className="ahw-compose">
                <div className="ahw-compose__head">
                  <h2>Add homework</h2>
                  <p>Write the task for this subject and date.</p>
                </div>
                <label className="ahw-label" htmlFor="ahw-text">
                  Homework text
                </label>
                <textarea
                  id="ahw-text"
                  className="ahw-textarea"
                  rows={5}
                  value={newHomework}
                  onChange={(e) => setNewHomework(e.target.value)}
                  placeholder="e.g. Complete exercise 4.2, questions 1–10"
                  disabled={busy}
                />
                <div className="ahw-compose__actions">
                  <button
                    type="button"
                    className="ahw-btn ahw-btn--primary"
                    onClick={handleAddHomework}
                    disabled={busy || !newHomework.trim()}
                  >
                    <FaPlus aria-hidden />
                    {saving ? "Saving…" : "Assign"}
                  </button>
                </div>
              </section>

              <section className="ahw-list">
                <div className="ahw-list__head">
                  <h2>Assigned homework</h2>
                  <p>
                    {subjectLabel} · {dateLabel}
                  </p>
                </div>

                {loading && !homework.length ? (
                  <div className="ahw-loading">
                    <MyLoader />
                  </div>
                ) : null}

                {!loading && !homework.length ? (
                  <div className="ahw-empty ahw-empty--soft">
                    No homework assigned for this subject yet.
                  </div>
                ) : (
                  <ul className="ahw-items">
                    {homework.map((item, index) => {
                      const imgUrl = resolveImgUrl(item.homework_img);
                      const isDeleting = deletingId === item.homework_id;
                      return (
                        <li
                          key={item.homework_id || index}
                          className="ahw-item"
                        >
                          <div className="ahw-item__main">
                            <span className="ahw-item__index" aria-hidden>
                              {index + 1}
                            </span>
                            <div className="ahw-item__body">
                              <p className="ahw-item__text">
                                {item.home_work || "—"}
                              </p>
                              {imgUrl ? (
                                <button
                                  type="button"
                                  className="ahw-item__thumb"
                                  onClick={() => setSelectedImage(imgUrl)}
                                  aria-label="View homework image"
                                >
                                  <img src={imgUrl} alt="" />
                                  <span>
                                    <FaImage aria-hidden /> View image
                                  </span>
                                </button>
                              ) : null}
                            </div>
                          </div>
                          <div className="ahw-item__actions">
                            <button
                              type="button"
                              className="ahw-icon-btn is-danger"
                              onClick={() => handleDeleteHomework(item)}
                              disabled={busy}
                              aria-label="Delete homework"
                              title="Delete"
                            >
                              <FaTrash aria-hidden />
                              {isDeleting ? "…" : null}
                            </button>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </section>
            </>
          )}
        </div>
      </div>

      {selectedImage ? (
        <div
          className="ahw-modal"
          role="dialog"
          aria-modal="true"
          aria-label="Homework image"
          onClick={() => setSelectedImage(null)}
        >
          <button
            type="button"
            className="ahw-modal__close"
            onClick={() => setSelectedImage(null)}
          >
            Close
          </button>
          <img
            src={selectedImage}
            alt="Homework"
            className="ahw-modal__img"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      ) : null}
    </div>
  );
}
